import type { InputHTMLAttributes } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconClose, IconSearch, InputField } from '@noths/polaris-client-ribbons-design-system';
import { ICON_BUTTON_TEXT_CLASS, iconButton } from '@noths/polaris-client-styles';

import * as styles from './SearchField.styles';

export interface SearchFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  ariaAutocomplete?: 'list' | 'none' | 'both' | 'inline';
  ariaExpanded?: boolean;
  ariaOwns?: string;
  displayValue?: string;
  id: string;
  onChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onReset: () => void;
  placeholder: string;
  showSubmitButtonAsFocused?: boolean;
  value: string;
}

export const SearchField = ({
  ariaAutocomplete,
  ariaExpanded,
  ariaOwns,
  displayValue,
  id,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onReset,
  placeholder,
  showSubmitButtonAsFocused,
  value,
}: SearchFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isValid, setIsValid] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  const handleFocus = useCallback(
    (evt: React.FocusEvent<HTMLInputElement>) => {
      setHasFocus(true);
      onFocus?.(evt);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (evt: React.FocusEvent<HTMLInputElement>) => {
      setHasFocus(false);
      onBlur?.(evt);
    },
    [onBlur],
  );

  const onResetInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    onReset();
  }, [onReset]);

  useEffect(() => {
    setIsValid(!!value.trim().length);
  }, [value]);

  return (
    <div css={styles.searchFieldContainer}>
      <InputField
        aria-autocomplete={ariaAutocomplete}
        aria-expanded={ariaExpanded}
        aria-owns={ariaOwns}
        autoComplete="off"
        id={id}
        label="Search"
        labelHidden
        name="term"
        onBlur={handleBlur}
        onChange={onChange}
        onFocus={handleFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        ref={inputRef}
        role="combobox"
        type="text"
        value={displayValue || value}
      />
      <div css={styles.buttonContainer}>
        {value && (
          <button
            css={[iconButton, styles.clear]}
            data-testid="clear"
            onClick={onResetInput}
            type="reset"
          >
            <IconClose css={styles.icon} />
            <span className={ICON_BUTTON_TEXT_CLASS}>clear</span>
          </button>
        )}
        <button
          css={[
            iconButton,
            styles.submit,
            (hasFocus || showSubmitButtonAsFocused) && styles.focusSearchIconSubmit,
          ]}
          disabled={!isValid}
          type="submit"
        >
          <IconSearch css={styles.icon} />
          <span className={ICON_BUTTON_TEXT_CLASS}>search</span>
        </button>
      </div>
    </div>
  );
};
